/**
 * WriteOffReasonsSettings — Feature 4: Write-Offs
 *
 * Settings card for managing write-off reasons and the "require reason" toggle.
 * Settings are persisted via the backend API.
 */

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { XCircle, Plus, Pencil, Trash2, Check, X, Star } from "lucide-react";
import { toast } from "sonner";
import {
  fetchWriteOffReasons,
  fetchBillingSettings,
  updateBillingSettings,
  createWriteOffReason,
  updateWriteOffReason as updateWriteOffReasonApi,
  deleteWriteOffReason as deleteWriteOffReasonApi,
} from "@/lib/data-service";
import type { WriteOffReason as ApiWriteOffReason } from "@/lib/data-service";

// ── Types ─────────────────────────────────────────────────────────────────────

interface WriteOffReason {
  id?: number;
  code: string;
  label: string;
  isDefault?: boolean;
  sortOrder?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface WriteOffReasonsSettingsProps {
  /** When true, renders content only (no Card wrapper) — for use inside AccordionSection */
  bare?: boolean;
}

export function WriteOffReasonsSettings({ bare = false }: WriteOffReasonsSettingsProps) {
  const [reasons, setReasons] = useState<WriteOffReason[]>([]);
  const [requireReason, setRequireReason] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [showAddRow, setShowAddRow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load from API on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [apiReasons, settings] = await Promise.all([
          fetchWriteOffReasons(),
          fetchBillingSettings(),
        ]);
        if (cancelled) return;
        setReasons(
          apiReasons.map((r: ApiWriteOffReason) => ({
            id: r.id,
            code: r.code,
            label: r.label,
            isDefault: r.isDefault,
            sortOrder: r.sortOrder,
          }))
        );
        setRequireReason(settings.requireWriteOffReason);
      } catch {
        if (cancelled) return;
        toast.error("Failed to load write-off settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleToggleRequire = async (checked: boolean) => {
    setSaving(true);
    try {
      await updateBillingSettings({ requireWriteOffReason: checked });
      setRequireReason(checked);
      toast.success(
        checked
          ? "Write-off reason is now required for all write-offs."
          : "Write-off reason is now optional."
      );
    } catch {
      toast.error("Failed to update setting.");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (reason: WriteOffReason) => {
    setEditingCode(reason.code);
    setEditingLabel(reason.label);
  };

  const handleSaveEdit = async () => {
    if (!editingLabel.trim()) return;
    const target = reasons.find((r) => r.code === editingCode);
    if (!target?.id) return;
    setSaving(true);
    try {
      await updateWriteOffReasonApi(target.id, {
        code: target.code,
        label: editingLabel.trim(),
        isDefault: target.isDefault,
        sortOrder: target.sortOrder,
      });
      setReasons((prev) =>
        prev.map((r) =>
          r.code === editingCode ? { ...r, label: editingLabel.trim() } : r
        )
      );
      setEditingCode(null);
      toast.success("Reason updated.");
    } catch {
      toast.error("Failed to update reason.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCode(null);
    setEditingLabel("");
  };

  const handleDelete = async (code: string) => {
    const target = reasons.find((r) => r.code === code);
    if (!target?.id) return;
    setSaving(true);
    try {
      await deleteWriteOffReasonApi(target.id);
      setReasons((prev) => prev.filter((r) => r.code !== code));
      toast.success("Reason removed.");
    } catch {
      toast.error("Failed to remove reason.");
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (code: string) => {
    const target = reasons.find((r) => r.code === code);
    if (!target?.id) return;
    setSaving(true);
    try {
      await updateWriteOffReasonApi(target.id, {
        code: target.code,
        label: target.label,
        isDefault: true,
        sortOrder: target.sortOrder,
      });
      setReasons((prev) =>
        prev.map((r) => ({ ...r, isDefault: r.code === code }))
      );
      toast.success("Default reason updated.");
    } catch {
      toast.error("Failed to update default reason.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddReason = async () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    const code = trimmed
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    if (reasons.some((r) => r.code === code)) {
      toast.error("A reason with a similar name already exists.");
      return;
    }
    setSaving(true);
    try {
      const created = await createWriteOffReason({
        code,
        label: trimmed,
        sortOrder: reasons.length + 1,
      });
      setReasons((prev) => [
        ...prev,
        { id: created.id, code: created.code, label: created.label, isDefault: created.isDefault, sortOrder: created.sortOrder },
      ]);
      setNewLabel("");
      setShowAddRow(false);
      toast.success("Write-off reason added.");
    } catch {
      toast.error("Failed to add reason.");
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div className="space-y-6">
        {/* Require reason checkbox */}
        <div className="flex items-start gap-3 rounded-lg border p-4">
          <Checkbox
            id="require-reason-toggle"
            checked={requireReason}
            onCheckedChange={(checked) => handleToggleRequire(checked === true)}
            className="mt-0.5"
          />
          <div className="space-y-1">
            <Label htmlFor="require-reason-toggle" className="text-sm font-medium cursor-pointer">
              Require write-off reason
            </Label>
            <p className="text-xs text-muted-foreground">
              When enabled, users must select a reason before confirming any write-off.
            </p>
          </div>
        </div>

        <Separator />

        {/* Reasons list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              Reason codes{" "}
              <span className="text-muted-foreground font-normal">
                ({reasons.length})
              </span>
            </p>
            {!showAddRow && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddRow(true)}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add reason
              </Button>
            )}
          </div>

          <div className="rounded-lg border divide-y">
            {reasons.map((reason) => (
              <div
                key={reason.code}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                {editingCode === reason.code ? (
                  /* Inline edit row */
                  <>
                    <Input
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit();
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                      className="h-7 text-sm flex-1"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-success-600"
                      onClick={handleSaveEdit}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={handleCancelEdit}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  /* Display row */
                  <>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm">{reason.label}</span>
                      {reason.isDefault && (
                        <Badge
                          variant="secondary"
                          className="ml-2 text-xs h-4 px-1.5"
                        >
                          default
                        </Badge>
                      )}
                    </div>
                    <span className="font-mono text-xs text-muted-foreground hidden sm:block">
                      {reason.code}
                    </span>

                    {/* Set as default */}
                    {!reason.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-warning-500"
                        title="Set as default"
                        onClick={() => handleSetDefault(reason.code)}
                      >
                        <Star className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    {/* Edit */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground"
                      onClick={() => handleStartEdit(reason)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>

                    {/* Delete */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove reason?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{reason.label}" will be removed from the write-off reasons
                            list. Existing write-offs using this reason will not be
                            affected.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(reason.code)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            ))}

            {/* Add new reason row */}
            {showAddRow && (
              <div className="flex items-center gap-2 px-3 py-2.5">
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="New reason label..."
                  className="h-7 text-sm flex-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddReason();
                    if (e.key === "Escape") {
                      setShowAddRow(false);
                      setNewLabel("");
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-success-600"
                  onClick={handleAddReason}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    setShowAddRow(false);
                    setNewLabel("");
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {reasons.length === 0 && !showAddRow && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No reasons configured. Add one above.
              </div>
            )}
          </div>
        </div>
    </div>
  );

  if (bare) return content;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <XCircle className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-base">Write-Off Reasons</CardTitle>
            <CardDescription className="mt-1">
              Manage the list of reasons available when writing off WIP items. Reasons help with reporting and audit trails.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
